import { useState } from "react";
import {
  Image,
  AlertCircle,
  Plus,
  ToggleLeft,
  ToggleRight,
  Pencil,
  ExternalLink,
  Monitor,
  Smartphone,
  GripVertical,
  Eye,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard, AdminModal, EmptyState } from "@/components/admin/shared";
import { MarketingImageEditor } from "@/components/admin/images";
import {
  useBanners,
  useCreateBanner,
  useUpdateBanner,
  useToggleBanner,
} from "@/hooks/admin/useMarketing";
import type {
  Banner,
  BannerPosition,
  CreateBannerInput,
  BannerListParams,
} from "@/lib/admin-api/marketing";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POSITION_LABELS: Record<BannerPosition, string> = {
  home_hero: "Accueil \u2014 Hero",
  home_mid: "Accueil \u2014 Milieu",
  category: "Page categorie",
  checkout: "Checkout",
};

const POSITION_STYLES: Record<BannerPosition, string> = {
  home_hero: "bg-purple-100 text-purple-800",
  home_mid: "bg-blue-100 text-blue-800",
  category: "bg-teal-100 text-teal-800",
  checkout: "bg-orange-100 text-orange-800",
};

const POSITION_ICONS: Record<BannerPosition, string> = {
  home_hero: "Hero principal",
  home_mid: "Section milieu",
  category: "Catégorie",
  checkout: "Tunnel d'achat",
};

// ---------------------------------------------------------------------------
// Banner form modal
// ---------------------------------------------------------------------------

interface BannerFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly initial?: Partial<Banner>;
}

function BannerFormModal({ open, onClose, initial }: BannerFormModalProps) {
  const isEdit = initial?.id != null;
  const create = useCreateBanner();
  const update = useUpdateBanner();

  const [title, setTitle] = useState(initial?.title ?? "");
  const [imageUrl, setImageUrl] = useState(initial?.image_url ?? "");
  const [linkUrl, setLinkUrl] = useState(initial?.link_url ?? "");
  const [position, setPosition] = useState<BannerPosition>(
    initial?.position ?? "home_hero",
  );
  const [startsAt, setStartsAt] = useState(
    initial?.starts_at?.slice(0, 10) ?? "",
  );
  const [endsAt, setEndsAt] = useState(initial?.ends_at?.slice(0, 10) ?? "");

  const isPending = create.isPending || update.isPending;

  const handleSubmit = () => {
    if (!title.trim()) {
      toast.error("Le titre est obligatoire.");
      return;
    }
    if (!imageUrl.trim()) {
      toast.error("L'image est obligatoire.");
      return;
    }
    if (!linkUrl.trim()) {
      toast.error("L'URL de destination est obligatoire.");
      return;
    }

    const input: CreateBannerInput = {
      title,
      image_url: imageUrl,
      link_url: linkUrl,
      position,
      starts_at: startsAt || null,
      ends_at: endsAt || null,
    };

    if (isEdit && initial?.id) {
      update.mutate(
        { id: initial.id, input },
        {
          onSuccess: () => {
            toast.success("Banniere mise a jour.");
            onClose();
          },
          onError: () => toast.error("Erreur lors de la mise a jour."),
        },
      );
    } else {
      create.mutate(input, {
        onSuccess: () => {
          toast.success("Banniere creee.");
          onClose();
        },
        onError: () => toast.error("Erreur lors de la creation."),
      });
    }
  };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={isEdit ? "Modifier le bandeau" : "Nouveau bandeau"}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? "En cours..." : isEdit ? "Mettre a jour" : "Creer"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Title */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Titre <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="ex : Offre speciale Ramadan"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>

        {/* Image upload + editor */}
        <MarketingImageEditor
          preset="banner"
          value={imageUrl}
          onChange={(url) => setImageUrl(url)}
          label="Image de la bannière"
        />

        {/* Link URL */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            URL de destination <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://..."
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>

        {/* Position as cards */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-stone-700">
            Position <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(POSITION_LABELS) as BannerPosition[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPosition(p)}
                className={`rounded-lg border p-3 text-left transition-all ${
                  position === p
                    ? "border-amber-400 bg-amber-50 ring-1 ring-amber-300"
                    : "border-stone-200 bg-white hover:border-stone-300"
                }`}
              >
                <p className="text-sm font-medium text-stone-800">
                  {POSITION_LABELS[p]}
                </p>
                <p className="text-xs text-stone-500 mt-0.5">
                  {POSITION_ICONS[p]}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Schedule */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Debut (optionnel)
            </label>
            <input
              type="date"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Fin (optionnel)
            </label>
            <input
              type="date"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              min={startsAt || undefined}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
        </div>

        {/* Display variant hint */}
        <div className="rounded-lg bg-stone-50 px-4 py-3 space-y-1">
          <div className="flex items-center gap-2">
            <Monitor className="h-4 w-4 text-stone-500" />
            <Smartphone className="h-4 w-4 text-stone-500" />
            <span className="text-xs font-medium text-stone-600">
              Variantes responsive
            </span>
          </div>
          <p className="text-xs text-stone-400">
            L'image sera automatiquement adaptee pour desktop et mobile. Pour de
            meilleurs resultats, utilisez une image 1200x400px minimum.
          </p>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Banner card
// ---------------------------------------------------------------------------

function BannerCard({
  banner,
  onEdit,
  onToggle,
}: {
  readonly banner: Banner;
  readonly onEdit: (b: Banner) => void;
  readonly onToggle: (id: string) => void;
}) {
  const ctr =
    banner.impressions > 0
      ? ((banner.clicks / banner.impressions) * 100).toFixed(1)
      : "0.0";

  return (
    <div
      className={`group rounded-xl border bg-white overflow-hidden transition-shadow hover:shadow-md ${
        banner.is_active ? "border-stone-200" : "border-stone-100 opacity-70"
      }`}
    >
      {/* Image preview */}
      {banner.image_url && (
        <div className="relative aspect-[3/1] bg-stone-100 overflow-hidden">
          <img
            src={banner.image_url}
            alt={`Bannière : ${banner.title}`}
            width={600}
            height={200}
            className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          {!banner.is_active && (
            <div className="absolute inset-0 bg-stone-900/30 flex items-center justify-center">
              <span className="rounded-full bg-stone-900/60 px-3 py-1 text-xs font-medium text-white">
                Inactif
              </span>
            </div>
          )}
        </div>
      )}

      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1 min-w-0">
            <p className="font-medium text-stone-900 leading-snug truncate">
              {banner.title}
            </p>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${POSITION_STYLES[banner.position]}`}
            >
              {POSITION_LABELS[banner.position]}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <a
              href={banner.link_url}
              target="_blank"
              rel="noreferrer"
              className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              title="Ouvrir le lien"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <button
              onClick={() => onEdit(banner)}
              className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              title="Modifier"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => onToggle(banner.id)}
              className={`rounded-md p-1.5 transition-colors ${
                banner.is_active
                  ? "text-emerald-600 hover:bg-emerald-50 hover:text-emerald-800"
                  : "text-stone-400 hover:bg-stone-100 hover:text-stone-600"
              }`}
              title={banner.is_active ? "Desactiver" : "Activer"}
            >
              {banner.is_active ? (
                <ToggleRight className="h-4 w-4" />
              ) : (
                <ToggleLeft className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {(banner.starts_at || banner.ends_at) && (
          <p className="text-xs text-stone-400">
            {banner.starts_at &&
              `Debut : ${new Date(banner.starts_at).toLocaleDateString("fr-SN")}`}
            {banner.starts_at && banner.ends_at && " \u00b7 "}
            {banner.ends_at &&
              `Fin : ${new Date(banner.ends_at).toLocaleDateString("fr-SN")}`}
          </p>
        )}

        <div className="flex gap-4 border-t border-stone-100 pt-3">
          <div className="space-y-0.5">
            <p className="text-xs text-stone-400">Impressions</p>
            <p className="tabular-nums text-sm font-semibold text-stone-800">
              {banner.impressions.toLocaleString("fr-SN")}
            </p>
          </div>
          <div className="space-y-0.5">
            <p className="text-xs text-stone-400">Clics</p>
            <p className="tabular-nums text-sm font-semibold text-stone-800">
              {banner.clicks.toLocaleString("fr-SN")}
            </p>
          </div>
          <div className="space-y-0.5">
            <p className="text-xs text-stone-400">Taux clic</p>
            <p className="tabular-nums text-sm font-semibold text-stone-800">
              {ctr} %
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Banners() {
  const [filterParams, setFilterParams] = useState<BannerListParams>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Banner | null>(null);

  const { data, isLoading, isError } = useBanners(filterParams);
  const toggle = useToggleBanner();

  const banners = data?.banners ?? [];

  const totalImpressions = banners.reduce((acc, b) => acc + b.impressions, 0);
  const totalClicks = banners.reduce((acc, b) => acc + b.clicks, 0);
  const avgCtr =
    totalImpressions > 0
      ? ((totalClicks / totalImpressions) * 100).toFixed(1)
      : "0.0";

  const handleToggle = (id: string) => {
    toggle.mutate(id, {
      onSuccess: () => toast.success("Statut mis a jour."),
      onError: () => toast.error("Erreur lors du changement de statut."),
    });
  };

  const handleEdit = (b: Banner) => {
    setEditTarget(b);
    setModalOpen(true);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Image className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Bannieres
              </h1>
              <p className="text-sm text-stone-500">
                Gestion des bandeaux promotionnels du site
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              setEditTarget(null);
              setModalOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Nouveau bandeau
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard label="Bannieres actives" value={banners.filter(b => b.is_active).length} format="number" />
          <KPICard label="Impressions totales" value={totalImpressions} format="number" icon={<Eye className="h-4 w-4" />} />
          <KPICard label="Clics totaux" value={totalClicks} format="number" />
          <KPICard label="Taux clic moyen" value={`${avgCtr} %`} />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select
            value={filterParams.position ?? ""}
            onChange={(e) =>
              setFilterParams({
                ...filterParams,
                position: (e.target.value as BannerPosition) || undefined,
              })
            }
            className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
          >
            <option value="">Toutes les positions</option>
            {(Object.keys(POSITION_LABELS) as BannerPosition[]).map((p) => (
              <option key={p} value={p}>
                {POSITION_LABELS[p]}
              </option>
            ))}
          </select>
          <select
            value={
              filterParams.is_active === undefined
                ? ""
                : filterParams.is_active
                ? "active"
                : "inactive"
            }
            onChange={(e) =>
              setFilterParams({
                ...filterParams,
                is_active:
                  e.target.value === ""
                    ? undefined
                    : e.target.value === "active",
              })
            }
            className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="inactive">Inactifs</option>
          </select>
        </div>

        {/* Content */}
        {isLoading && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 animate-pulse">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-52 rounded-xl bg-stone-100" />
            ))}
          </div>
        )}

        {isError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Impossible de charger les bannieres.
          </div>
        )}

        {!isLoading && !isError && banners.length === 0 && (
          <EmptyState
            icon={<Image className="h-6 w-6" />}
            title="Aucune banniere"
            description="Creez votre premier bandeau promotionnel."
            action={{
              label: "Nouveau bandeau",
              onClick: () => {
                setEditTarget(null);
                setModalOpen(true);
              },
            }}
          />
        )}

        {!isLoading && !isError && banners.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {banners.map((b) => (
              <BannerCard
                key={b.id}
                banner={b}
                onEdit={handleEdit}
                onToggle={handleToggle}
              />
            ))}
          </div>
        )}
      </div>

      <BannerFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initial={editTarget ?? undefined}
      />
    </AdminLayout>
  );
}
